import React, {useEffect, useState} from 'react';
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import ApiManager from "hcla-web-frontend-primitives/app/Api/ApiManager";
import DialogContent from "@material-ui/core/DialogContent";
import ConfigForm from "bayesian-estimation-web/app/components/Model/SingleBayesianModelView/InputDrawer/ConfigForm";
import BayesianEstimationResultsDialog from "./BayesianEstimationResultsDialog";
import BasicEventVertex from "../../mxGraph/HCLTreeNode/FaultTreeNode/BasicEventNode/BasicEventVertex";
import ProxyTypes from "../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import {DialogActions} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CancelButton from "hcla-web-frontend-primitives/app/components/primitives/Buttons/CancelButton";
import {FaultTreeMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/FaultTreeMxGraphEditorView";
import {EventTreeMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/EventTreeMxGraphEditorView";
import {BayesianNetworkMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/BayesianNetworkMxGraphEditorView";
import AuthService from 'hcla-web-frontend-primitives/app/Api/AuthService';
import GenericSnackbar, {SEVERITY} from "hcla-web-frontend-primitives/app/components/primitives/Snackbar/GenericSnackbar";

interface Props {
	data: BasicEventVertex;
	treeData: any;
	save: (e: any, json: FaultTreeMxGraphViewJSON | EventTreeMxGraphViewJSON | BayesianNetworkMxGraphViewJSON) => void;
	close: () => void;
}

interface SnackBar {
	show: boolean;
	message: string;
}

const BayesianEstimationDialog = (props: Props) => {
	const { close, data, treeData, save } = props;

	const [modelSpecificDetails, setModelSpecificDetails] = useState<any>(null);
	const [output, setOutput] = useState(null);
	const [isSavingInProgress, setIsSavingInProgress] = useState(false);
	const [isQuantificationInProgress, setIsQuantificationInProgress] = useState(false);
	const [snackBar, setSnackBar] = useState<SnackBar>({ show: false, message: "N/A" });
	useEffect(() => {
		ApiManager.postNewModel("bayesian", JSON.stringify({
			title: `${treeData.label.name} - ${treeData.model_tree_id}`,
			assigned_users: [AuthService.getProfile().user_id]
		}))
			.then(response => response.status === 201 && response.json())
			.then(res => {
				const value = data.value;
				const expression = value.getExpression();
				const extendedJSON = expression.toExtendedJSON();
				const proxy = expression.getProxy();
				const params = proxy === ProxyTypes.NORMAL_DISTRIBUTION ? extendedJSON.normal_params : extendedJSON.log_normal_params;
				const paramsToRender = () => {
					switch (params) {
						case "mean & std":
							return "Mean & Standard-Deviation";
						case "percentiles":
							return "Percentile";
						case "mean & error factor":
							return "Mean & Error-Factor";
						case "median & error factor":
							return "Median & Error-Factor";
						default:
							return null;
					}
				}
				const valueToRender = () => {
					switch (params) {
						case "mean & std":
							return [extendedJSON.normal_mean, extendedJSON.normal_std];
						case "percentiles":
							if (proxy === ProxyTypes.NORMAL_DISTRIBUTION) {
								return [extendedJSON.normal_p5, extendedJSON.normal_p95];
							}
							return [extendedJSON.log_normal_p5, extendedJSON.log_normal_p95];
						case "mean & error factor":
							return [extendedJSON.log_normal_mean, extendedJSON.log_normal_error_factor];
						case "median & error factor":
							return [extendedJSON.log_normal_median, extendedJSON.log_normal_error_factor];
						default:
							return null;
					}
				}
				setModelSpecificDetails({
					...res,
					model_data: {
						...res.model_data,
						model_type: "Homogeneous",
						prior: {
							...res.model_data.prior,
							dist: proxy === ProxyTypes.NORMAL_DISTRIBUTION ? "Normal" : "Log-Normal",
							params_type: paramsToRender(),
							params: {
								...res.model_data.prior.params,
								values: valueToRender(),
							}
						},
						likelihood: {
							dist: null,
							params_type: null,
							params: {
								values: [],
								component_results: null
							}
						}
					}
				});
			});
	},[]);

	const shouldEstimateBeDisabled = () => {
		if (modelSpecificDetails) {
			const { model_data } = modelSpecificDetails;
			return !model_data.prior.dist ||
				!model_data.prior.params_type ||
				model_data.prior.params.values.length === 0 ||
				!model_data.likelihood.dist ||
				!model_data.likelihood.params_type ||
				model_data.likelihood.params.values.length === 0
		}
	}

	const hasModelBeenQuantified = () => {
		try {
			return modelSpecificDetails.results[0] !== undefined;
		} catch (e) {
			return false;
		}
	};

	const onSubmit = () => {
		setIsSavingInProgress(true);
	};

	useEffect(() => {
		if (isSavingInProgress) {
			const payload = {
				model_data: modelSpecificDetails.model_data,
			};
			ApiManager.patchBEModel(modelSpecificDetails.id, JSON.stringify(payload))
				.then(response => response.status === 200 && response.json())
				.then((res) => {
					setModelSpecificDetails(res);
				})
				.then(() => setIsSavingInProgress(false))
				.then(() => setIsQuantificationInProgress(true));
		}
	},[isSavingInProgress]);

	useEffect(() => {
		if (modelSpecificDetails && isQuantificationInProgress) {
			if (hasModelBeenQuantified()) {
				ApiManager.reQuantifyBEModel(modelSpecificDetails.id)
					.then(response => response.json())
					.then(res => setOutput(res))
					.then(() => setIsQuantificationInProgress(false))
					.catch(e => {
						setIsQuantificationInProgress(false)
						setSnackBar({ show: true, message: "Could not quantify."})
					});
			} else {
				ApiManager.quantifyBEModel(modelSpecificDetails.id)
					.then(response => response.json())
					.then(res => setOutput(res))
					.then(() => setIsQuantificationInProgress(false))
					.catch(e => {
						setIsQuantificationInProgress(false)
						setSnackBar({ show: true, message: "Could not quantify."})
					});
			}
		}
	},[isQuantificationInProgress]);

	/**
	 * When model_type is updated, set corresponding prior and likelihood variables to default null.
	 * @param e The window event
	 * @param value String value of model_type: must be one of [Homogeneous, Non-Homogeneous]
	 */
	const updateModelType = (e, value) => {
		// do nothing if the same selection is made
		if (modelSpecificDetails.model_data.model_type === value) {
			return;
		}

		if (
			modelSpecificDetails.model_data.model_type === "Homogeneous" &&
			modelSpecificDetails.model_data.prior.dist !== "Log-Normal" &&
			modelSpecificDetails.model_data.prior.params_type !== "Median & Error-Factor"
		) {
			setSnackBar({ show: true, message: "Prior parameters are incompatible with Non Homogeneous Model Type"})
			return;
		}

		if (
			modelSpecificDetails.model_data.prior.dist === "Log-Normal" &&
			modelSpecificDetails.model_data.prior.params_type === "Median & Error-Factor" || modelSpecificDetails.model_data.prior.params_type === "Median & Error-Factor (estimates)"
		) {
			const isNonHomogeneousParamsType = modelSpecificDetails.model_data.prior.params_type === "Median & Error-Factor (estimates)";
			setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					model_type: value,
					prior: {
						dist: "Log-Normal",
						params_type: isNonHomogeneousParamsType ? "Median & Error-Factor" : "Median & Error-Factor (estimates)",
						params: {
							values: modelSpecificDetails.model_data.prior.params.values,
							component_result: null,
						},
					},
					likelihood: {
						dist: null,
						params_type: null,
						params: {
							values: [],
							component_results: [],
						},
					},
				},
			});
		} else {
			setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					model_type: value,
					prior: {
						dist: null,
						params_type: null,
						params: {
							values: [],
							component_result: null,
						},
					},
					likelihood: {
						dist: null,
						params_type: null,
						params: {
							values: [],
							component_results: [],
						},
					},
				},
			});
		}
	};

	/**
	 * Update the sample count
	 * @param e The window event
	 * @param value String or Number value for sample count
	 */
	const updateSampleCount = (e, value) => {
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					samples: Number(value),
				},
			});
	};

	/**
	 * Update the number of points
	 * @param e The window event
	 * @param value String or Number value for the number of points
	 */
	const updateNumPoints = (e, value) => {
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					number_of_pts: Number(value),
				},
			});
	};


	/**
	 * Update the prior dist, setting params_type and params:{ values, component_result } to null.
	 * @param e The window event
	 * @param value String prior distribution
	 */
	const updatePriorDist = (e, value) => {
		// do nothing if the same selection is made
		if (modelSpecificDetails.model_data.prior.dist === value) {
			return;
		}

		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					prior: {
						dist: value,
						params_type: null,
						params: {
							values: [],
							component_result: null,
						},
					},
					likelihood: {
						dist: null,
						params_type: null,
						params: {
							values: [],
							component_results: [],
						},
					},
				},
			});
	};

	/**
	 * Update the prior params_type, setting params:{ values, component_result } to null.
	 * @param e The window event
	 * @param value String prior params type
	 */
	const updatePriorParamsType = (e, value) => {
		// do nothing if the same selection is made
		if (modelSpecificDetails.model_data.prior.params_type === value) {
			return;
		}

		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					prior: {
						...modelSpecificDetails.model_data.prior,
						params_type: value,
						params: {
							values: [],
							component_result: null,
						},
					},
					likelihood: {
						dist: null,
						params_type: null,
						params: {
							values: [],
							component_results: [],
						},
					},
				},
			});
	};

	/**
	 * Update the prior params value, setting params:{ component_result } to null.
	 * @param e the window event
	 * @param key
	 * @param values
	 */
	const updatePriorParamsValues = (e, key, values) => {
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					prior: {
						...modelSpecificDetails.model_data.prior,
						params: {
							values,
							component_result: null,
						},
					},
				},
			});
	};

	/**
	 * Update the prior params value to a component result, setting params:{ values } to empty.
	 * @param e The window event
	 * @param value Number indicating a valid component result
	 */
	const updatePriorParamsComponentResult = (e, value) => {
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					prior: {
						...modelSpecificDetails.model_data.prior,
						params: {
							component_result: Number(value),
							values: [],
						},
					},
				},
			});
	};

	/**
	 * Update the likelihood dist, setting rest of the likelihood params to null.
	 * @param e The window event
	 * @param value String likelihood distribution
	 */
	const updateLikelihoodDist = (e, value) => {
		// do nothing if the same selection is made
		if (modelSpecificDetails.model_data.likelihood.dist === value) {
			return;
		}

		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						dist: value,
						params_type: null,
						params: {
							values: [],
							component_results: [],
						},
					},
				},
			});
	};

	/**
	 * Update the likelihood params_type, setting params:{ values, component_result } to empty arrays.
	 * @param e The window event
	 * @param value String Likelihood params_type
	 */
	const updateLikelihoodParamsType = (e, value) => {
		// do nothing if the same selection is made
		if (modelSpecificDetails.model_data.likelihood.params_type === value) {
			return;
		}

		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params_type: value,
						// TODO:: check if these need to be cleared out!
						// params: {
						//   values: [],
						//   component_results: [],
						// },
					},
				},
			});
	};

	/**
	 * Update a likelihood params value in the likelihood params array
	 * @param e The window event
	 * @param key the index of the value to be updated
	 * @param value An array of N values indicating the likelihood params
	 */
	const updateLikelihoodParamsValue = (e, key, value) => {
		const updatedVals = modelSpecificDetails.model_data.likelihood.params.values;
		updatedVals[key] = value;
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params: {
							...modelSpecificDetails.model_data.likelihood.params,
							values: updatedVals,
						},
					},
				},
			});
	};

	/**
	 * Add to the likelihood params value array
	 * @param e The window event
	 * @param value An array of N values indicating the likelihood params
	 */
	const addLikelihoodParamsValue = (e, value) => {
		const updatedVals = modelSpecificDetails.model_data.likelihood.params.values;
		updatedVals.push(value);
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params: {
							...modelSpecificDetails.model_data.likelihood.params,
							values: updatedVals,
						},
					},
				},
			});
	};

	/**
	 * Add to the likelihood params value array
	 * @param e The window event
	 * @param list An array of N values indicating the likelihood params
	 */
	const addLikelihoodParamsList = (e, list) => {
		const updatedVals = modelSpecificDetails.model_data.likelihood.params.values;
		const updatedList = updatedVals.concat(list);
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params: {
							...modelSpecificDetails.model_data.likelihood.params,
							values: updatedList,
						},
					},
				},
			});
	};

	/**
	 * Remove from the likelihood params value array
	 * @param e The window event
	 * @param value An array of N values indicating the likelihood params
	 */
	const removeLikelihoodParamsValue = (e, key) => {
		const updatedVals = modelSpecificDetails.model_data.likelihood.params.values;
		updatedVals.splice(key, 1);
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params: {
							...modelSpecificDetails.model_data.likelihood.params,
							values: updatedVals,
						},
					},
				},
			});
	};

	/**
	 * Add to the likelihood params component result array
	 * @param e The window event
	 * @param value An array of N values indicating the likelihood params
	 */
	const addLikelihoodParamsComponentResult = (e, value) => {
		const updatedComponents = modelSpecificDetails.model_data.likelihood.params.component_results;
		updatedComponents.push(value);
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params: {
							...modelSpecificDetails.model_data.likelihood.params,
							component_results: updatedComponents,
						},
					},
				},
			});
	};

	/**
	 * Remove from the likelihood params component result array
	 * @param e The window event
	 * @param value An array of N values indicating the likelihood params
	 */
	const removeLikelihoodParamsComponentResult = (e, key) => {
		const updatedComponents = modelSpecificDetails.model_data.likelihood.params.component_results;
		updatedComponents.splice(key, 1);
		setModelSpecificDetails({
				...modelSpecificDetails,
				model_data: {
					...modelSpecificDetails.model_data,
					likelihood: {
						...modelSpecificDetails.model_data.likelihood,
						params: {
							...modelSpecificDetails.model_data.likelihood.params,
							component_results: updatedComponents,
						},
					},
				},
			});
	};

	const getPriorParamsValues = () => {
		try {
			return modelSpecificDetails.model_data.prior.params.values;
		} catch (e) {
			return [0, 0];
		}
	};

	const getPriorType = () => {
		try {
			return modelSpecificDetails.model_data.prior.params_type;
		} catch (e) {
			return null;
		}
	};

	const getPriorDist = () => {
		try {
			return modelSpecificDetails.model_data.prior.dist;
		} catch (e) {
			return null;
		}
	};

	const handleClose = () => {
		ApiManager.deleteModel(modelSpecificDetails.id)
			.then(response => response.status === 204)
			.then(close);
	}

	const handleSave = (e, json) => {
		ApiManager.deleteModel(modelSpecificDetails.id)
			.then(response => response.status === 204)
			.then(() => setOutput(null))
			.then(() => save(e, json));
	}

	return (
		<Dialog
			open={true}
			fullWidth
			maxWidth={false}
			onClose={handleClose}
		>
			<DialogTitle>Set Bayesian Estimation</DialogTitle>
			<DialogContent>
				{
					output && modelSpecificDetails && (
						<BayesianEstimationResultsDialog
							basicEvent={data}
							treeData={treeData}
							results={output}
							priorDist={getPriorDist()}
							priorType={getPriorType()}
							priorValues={getPriorParamsValues()}
							isQuantifying={isQuantificationInProgress}
							// isLoading={this.state.isQuantificationLoading}
							points={modelSpecificDetails.model_data.number_of_pts}
							save={handleSave}
							close={() => setOutput(null)}
						/>
					)
				}
				{
					modelSpecificDetails && (
						<ConfigForm
							selected={modelSpecificDetails.model_data}
							isSaving={isSavingInProgress}
							isQuantifying={isQuantificationInProgress}
							onUpdateModelType={updateModelType}
							onUpdateSampleCount={updateSampleCount}
							onUpdateNumPoints={updateNumPoints}
							onUpdatePriorDist={updatePriorDist}
							onUpdatePriorParamsType={updatePriorParamsType}
							onUpdatePriorParamsValues={updatePriorParamsValues}
							onUpdatePriorParamsComponentResult={updatePriorParamsComponentResult}
							onUpdateLikelihoodDist={updateLikelihoodDist}
							onUpdateLikelihoodParamsType={updateLikelihoodParamsType}
							onAddLikelihoodParamsValues={addLikelihoodParamsValue}
							onAddLikelihoodParamsList={addLikelihoodParamsList}
							onRemoveLikelihoodParamsValues={removeLikelihoodParamsValue}
							onUpdateLikelihoodParamsValues={updateLikelihoodParamsValue}
							onAddLikelihoodParamsComponentResult={addLikelihoodParamsComponentResult}
							onRemoveLikelihoodParamsComponentResult={removeLikelihoodParamsComponentResult}
							hideActions={true}
							disable={{
								prior_type: true,
								prior_parameter_type: true,
								values: true
							}}
						/>
					)
				}
			</DialogContent>
			<DialogActions>
				<CancelButton
					variant="outlined"
					size="large"
					onClick={handleClose}
				>
					Cancel
				</CancelButton>
				<Button
					color={"primary"}
					variant={"contained"}
					size={"large"}
					disabled={shouldEstimateBeDisabled()}
					onClick={() => onSubmit()}
				>
					Estimate
				</Button>
			</DialogActions>
			<GenericSnackbar
				showSnackBar={snackBar.show}
				onClose={() => setSnackBar({ show: false, message: "N/A" })}
				message={snackBar.message}
				severity={SEVERITY.ERROR}
			/>
		</Dialog>
	);
};

export default BayesianEstimationDialog;